import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import ChatBox from "../components/ChatBox";
import { apiFetch, getApiBase } from "../lib/apiBase";

/**
 * OBS Custom Browser Dock
 * URL: https://www.gastreaming.live/obs/chat/<channel>
 * Example: https://www.gastreaming.live/obs/chat/getactive_pet3
 *
 * Notes:
 * - No PageShell so we don't render the site header/background.
 * - Uses the same socket chat as the website.
 * - Posting still requires the user to be signed-in (JWT in localStorage).
 */

const SITE_AUTH_KEYS = [
  "token",
  "ga_token",
  "authToken",
  "jwt",
  "streamsite_token",
  "accessToken",
  "access_token",
  "auth_token",
  "site_token",
];

const OBS_ONLY_KEYS = [
  "obs_token",
  "obsDockToken",
  "obs_dock_token",
];

const AUTH_JSON_KEYS = [
  "auth",
  "authState",
  "auth_state",
  "session",
  "user",
  "currentUser",
  "streamsite_auth",
  "streamsite_session",
];

function extractTokenCandidate(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";

  if (
    raw.length > 20 &&
    !raw.startsWith("{") &&
    !raw.startsWith("[") &&
    !raw.includes("undefined") &&
    !raw.includes("null")
  ) {
    return raw;
  }

  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return "";

    const direct =
      parsed.token ||
      parsed.authToken ||
      parsed.jwt ||
      parsed.accessToken ||
      parsed.access_token ||
      parsed.auth_token ||
      parsed?.auth?.token ||
      parsed?.auth?.jwt ||
      parsed?.session?.token ||
      parsed?.session?.jwt ||
      parsed?.user?.token ||
      parsed?.user?.jwt ||
      "";

    return String(direct || "").trim();
  } catch {
    return "";
  }
}

function persistObsToken(token) {
  const t = String(token || "").trim();
  if (!t) return false;

  try {
    OBS_ONLY_KEYS.forEach((k) => {
      window.localStorage.setItem(k, t);
      try {
        window.sessionStorage.setItem(k, t);
      } catch {}
    });
    return true;
  } catch {
    return false;
  }
}

function readObsTokenFromUrl() {
  try {
    const qs = new URLSearchParams(window.location.search);
    return String(qs.get("token") || qs.get("auth") || "").trim();
  } catch {
    return "";
  }
}

function readTokenFromKeys(storageLike, keys) {
  if (!storageLike) return "";

  try {
    for (const key of keys) {
      const found = extractTokenCandidate(storageLike.getItem(key));
      if (found) return found;
    }
  } catch {
    // ignore
  }

  return "";
}

function readTokenFromJsonKeys(storageLike) {
  if (!storageLike) return "";

  try {
    for (const key of AUTH_JSON_KEYS) {
      const found = extractTokenCandidate(storageLike.getItem(key));
      if (found) return found;
    }
  } catch {
    // ignore
  }

  return "";
}

export default function ObsChatDock() {
  const { channel } = useParams();
  const slug = String(channel || "").trim() || "global";

  const readStoredObsToken = useCallback(() => {
    try {
      return (
        readTokenFromKeys(window.localStorage, OBS_ONLY_KEYS) ||
        readTokenFromKeys(window.sessionStorage, OBS_ONLY_KEYS) ||
        ""
      );
    } catch {
      return "";
    }
  }, []);

  const readAnyObsToken = useCallback(() => {
    return readObsTokenFromUrl() || readStoredObsToken() || "";
  }, [readStoredObsToken]);

  const readSiteAuthToken = useCallback(() => {
    try {
      return (
        readTokenFromKeys(window.localStorage, SITE_AUTH_KEYS) ||
        readTokenFromKeys(window.sessionStorage, SITE_AUTH_KEYS) ||
        readTokenFromJsonKeys(window.localStorage) ||
        readTokenFromJsonKeys(window.sessionStorage) ||
        ""
      );
    } catch {
      return "";
    }
  }, []);

  const [authed, setAuthed] = useState(() => {
    const urlToken = readObsTokenFromUrl();
    if (urlToken) persistObsToken(urlToken);
    return !!(urlToken || readStoredObsToken());
  });

  const [room, setRoom] = useState(() => `vod:${slug}`);
  const [metaTitle, setMetaTitle] = useState("");
  const [metaGame, setMetaGame] = useState("");
  const [saving, setSaving] = useState(false);
  const [viewerCount, setViewerCount] = useState(0);
  const [mintingObsToken, setMintingObsToken] = useState(false);

  useEffect(() => {
    document.body.classList.add("obs-dock");
    return () => document.body.classList.remove("obs-dock");
  }, []);

  useEffect(() => {
    const urlToken = readObsTokenFromUrl();
    if (urlToken) {
      persistObsToken(urlToken);
      setAuthed(true);
    } else {
      setAuthed(!!readAnyObsToken());
    }
  }, [readAnyObsToken]);

  useEffect(() => {
    let cancelled = false;

    const syncRoom = async () => {
      try {
        const data = await apiFetch(`/streams/${encodeURIComponent(slug)}`);
        const nextRoom = data?.stream?.chat?.room || data?.chat?.room;
        if (!cancelled && nextRoom) {
          setRoom((prev) => {
            const next = String(nextRoom);
            return prev === next ? prev : next;
          });
        }
      } catch {
        // Keep fallback room.
      }
    };

    syncRoom();
    const id = setInterval(syncRoom, 5000);

    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [slug]);

  useEffect(() => {
    let cancelled = false;

    const loadMeta = async () => {
      try {
        const data = await apiFetch(`/streams/meta/${encodeURIComponent(slug)}`);
        const m = data?.meta || {};
        if (cancelled) return;
        setMetaTitle(String(m?.title || ""));
        setMetaGame(String(m?.game || ""));
      } catch {
        // ignore
      }
    };

    loadMeta();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  useEffect(() => {
    let cancelled = false;
    const roomKey = `live:${slug}`;

    const tick = async () => {
      try {
        const data = await apiFetch(`/viewers?room=${encodeURIComponent(roomKey)}`);
        if (!cancelled && data && data.ok) {
          setViewerCount(Number(data?.count || 0));
        }
      } catch {
        // ignore
      }
    };

    tick();
    const id = setInterval(tick, 1500);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [slug]);

  async function mintFreshObsToken() {
    const siteToken = readSiteAuthToken();
    if (!siteToken) {
      throw new Error("missing_site_token");
    }

    const data = await apiFetch("/obs/token", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${siteToken}`,
      },
    });

    const fresh = String(data?.token || "").trim();
    if (!fresh) {
      throw new Error("missing_obs_token");
    }

    persistObsToken(fresh);
    setAuthed(true);
    return fresh;
  }

  async function saveMeta() {
    let token = readAnyObsToken();

    if (!token) {
      try {
        setSaving(true);
        token = await mintFreshObsToken();
      } catch (e) {
        const msg = String(e?.message || "");
        if (msg === "missing_site_token") {
          alert("No signed-in site token was found on this host. Sign in again on this exact domain, then try again.");
        } else {
          alert("Could not create a fresh OBS token.");
        }
        setSaving(false);
        return;
      }
    }

    try {
      setSaving(true);
      await apiFetch(`/streams/meta/${encodeURIComponent(slug)}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ title: metaTitle, game: metaGame }),
      });
    } catch (e) {
      const msg = String(e?.message || "Failed to save");
      if (e?.status === 401 || /invalid auth token|expired token|invalid_token/i.test(msg)) {
        alert("OBS dock token expired. Copy the fresh OBS dock URL from this page and re-open that browser dock in OBS.");
      } else {
        alert(msg);
      }
    } finally {
      setSaving(false);
    }
  }

  function copy(text, emptyMessage) {
    const value = String(text || "").trim();
    if (!value) {
      if (emptyMessage) alert(emptyMessage);
      return;
    }

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(value).catch(() => {
        window.prompt("Copy this:", value);
      });
    } else {
      window.prompt("Copy this:", value);
    }
  }

  async function handleCopyObsDockUrl() {
    try {
      setMintingObsToken(true);
      const fresh = await mintFreshObsToken();
      const origin = typeof window !== "undefined" ? window.location.origin : "";
      const url = `${origin}/obs/chat/${encodeURIComponent(slug)}?role=obs&token=${encodeURIComponent(fresh)}`;
      copy(url, "Could not generate OBS dock URL.");
    } catch (e) {
      const msg = String(e?.message || "");
      if (msg === "missing_site_token") {
        alert("No signed-in site token was found on this host. Sign in again on this exact domain, then try COPY OBS DOCK URL again.");
      } else {
        alert("Could not generate a fresh OBS dock URL.");
      }
    } finally {
      setMintingObsToken(false);
    }
  }

  const apiBase = useMemo(() => {
    try {
      return String(getApiBase() || "").replace(/\/+$/, "");
    } catch {
      return "";
    }
  }, []);

  const viewersWidgetUrl = useMemo(() => {
    return `${apiBase}/overlay/viewers/${encodeURIComponent(slug)}`;
  }, [apiBase, slug]);

  const metaWidgetUrl = useMemo(() => {
    return `${apiBase}/overlay/meta/${encodeURIComponent(slug)}`;
  }, [apiBase, slug]);

  const gasPointsOverlayUrl = useMemo(() => {
    const origin =
      typeof window !== "undefined"
        ? String(window.location.origin || "").replace(/\/+$/, "")
        : "";
    return `${origin}/overlay/gas-points/${encodeURIComponent(slug)}?volume=1`;
  }, [slug]);

  const tournamentPromoOverlayUrl = useMemo(() => {
    const origin =
      typeof window !== "undefined"
        ? String(window.location.origin || "").replace(/\/+$/, "")
        : "";
    return `${origin}/overlay/tournament-promo/${encodeURIComponent(slug)}`;
  }, [slug]);

  const hasObsToken = !!readAnyObsToken();

  return (
    <div
      style={{
        height: "100vh",
        width: "100vw",
        padding: 10,
        display: "flex",
        flexDirection: "column",
        gap: 10,
        background: "transparent",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          padding: "10px 12px",
          borderRadius: 12,
          border: "1px solid rgba(255,255,255,0.10)",
          background: "rgba(0,0,0,0.55)",
        }}
      >
        <div style={{ fontWeight: 900, letterSpacing: 0.5 }}>
          GA Chat • {slug}
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <div style={{ opacity: 0.9, fontWeight: 900, fontSize: 12 }}>
            👀 {viewerCount}
          </div>
          <div style={{ opacity: 0.8, fontWeight: 800, fontSize: 12 }}>OBS Dock</div>
        </div>
      </div>

      <div
        style={{
          padding: 12,
          borderRadius: 12,
          border: "1px solid rgba(255,255,255,0.10)",
          background: "rgba(0,0,0,0.45)",
          display: "grid",
          gridTemplateColumns: "1fr 1fr auto",
          gap: 10,
          alignItems: "end",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={{ fontSize: 12, opacity: 0.85, fontWeight: 900 }}>TITLE</div>
          <input
            value={metaTitle}
            onChange={(e) => setMetaTitle(e.target.value)}
            placeholder="e.g. Road to Legend"
            style={{
              height: 38,
              borderRadius: 10,
              border: "1px solid rgba(255,255,255,0.14)",
              background: "rgba(255,255,255,0.06)",
              color: "white",
              padding: "0 10px",
              outline: "none",
              fontWeight: 800,
            }}
          />
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={{ fontSize: 12, opacity: 0.85, fontWeight: 900 }}>GAME</div>
          <input
            value={metaGame}
            onChange={(e) => setMetaGame(e.target.value)}
            placeholder="e.g. NBA 2K"
            style={{
              height: 38,
              borderRadius: 10,
              border: "1px solid rgba(255,255,255,0.14)",
              background: "rgba(255,255,255,0.06)",
              color: "white",
              padding: "0 10px",
              outline: "none",
              fontWeight: 800,
            }}
          />
        </div>

        <button
          type="button"
          onClick={saveMeta}
          disabled={saving}
          style={{
            height: 38,
            padding: "0 14px",
            borderRadius: 10,
            border: "1px solid rgba(255,255,255,0.14)",
            background: "rgba(255,255,255,0.10)",
            color: "white",
            cursor: saving ? "not-allowed" : "pointer",
            fontWeight: 900,
            opacity: saving ? 0.65 : 1,
            letterSpacing: 0.6,
          }}
        >
          {saving ? "SAVING..." : "SAVE"}
        </button>

        <div
          style={{
            gridColumn: "1 / -1",
            display: "flex",
            flexWrap: "wrap",
            gap: 10,
            alignItems: "center",
            justifyContent: "space-between",
            paddingTop: 6,
            borderTop: "1px solid rgba(255,255,255,0.10)",
          }}
        >
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={handleCopyObsDockUrl}
              disabled={mintingObsToken}
              style={{
                height: 32,
                padding: "0 12px",
                borderRadius: 10,
                border: "1px solid rgba(255,255,255,0.14)",
                background: hasObsToken ? "rgba(0,0,0,0.35)" : "rgba(120,0,0,0.35)",
                color: "white",
                cursor: mintingObsToken ? "not-allowed" : "pointer",
                fontWeight: 900,
                fontSize: 12,
                letterSpacing: 0.6,
                opacity: mintingObsToken ? 0.65 : 1,
              }}
              title="OBS custom browser dock URL with fresh auth token"
            >
              {mintingObsToken ? "GENERATING..." : "COPY OBS DOCK URL"}
            </button>

            <button
              type="button"
              onClick={() => copy(viewersWidgetUrl)}
              style={{
                height: 32,
                padding: "0 12px",
                borderRadius: 10,
                border: "1px solid rgba(255,255,255,0.14)",
                background: "rgba(0,0,0,0.35)",
                color: "white",
                cursor: "pointer",
                fontWeight: 900,
                fontSize: 12,
                letterSpacing: 0.6,
              }}
              title="Browser Source URL for viewer widget"
            >
              COPY VIEWERS WIDGET URL
            </button>

            <button
              type="button"
              onClick={() => copy(metaWidgetUrl)}
              style={{
                height: 32,
                padding: "0 12px",
                borderRadius: 10,
                border: "1px solid rgba(255,255,255,0.14)",
                background: "rgba(0,0,0,0.35)",
                color: "white",
                cursor: "pointer",
                fontWeight: 900,
                fontSize: 12,
                letterSpacing: 0.6,
              }}
              title="Browser Source URL for title/game widget"
            >
              COPY TITLE/GAME WIDGET URL
            </button>

            <button
              type="button"
              onClick={() => copy(gasPointsOverlayUrl)}
              style={{
                height: 32,
                padding: "0 12px",
                borderRadius: 10,
                border: "1px solid rgba(255,214,92,0.34)",
                background: "linear-gradient(135deg, rgba(157,91,0,0.72), rgba(150,13,32,0.72))",
                color: "white",
                cursor: "pointer",
                fontWeight: 900,
                fontSize: 12,
                letterSpacing: 0.6,
              }}
              title="Transparent OBS Browser Source for GAS Points message and emote alerts with sound"
            >
              COPY GAS POINTS ALERT URL
            </button>

            <button
              type="button"
              onClick={() => copy(tournamentPromoOverlayUrl)}
              style={{
                height: 32,
                padding: "0 12px",
                borderRadius: 10,
                border: "1px solid rgba(255,214,92,0.48)",
                background: "linear-gradient(135deg, rgba(120,0,16,0.92), rgba(181,103,0,0.82))",
                color: "white",
                cursor: "pointer",
                fontWeight: 900,
                fontSize: 12,
                letterSpacing: 0.6,
              }}
              title="Transparent 1920×1080 starting-scene promo with the tournament registration URL"
            >
              COPY TOURNAMENT PROMO URL
            </button>
          </div>

          <div style={{ opacity: 0.85, fontWeight: 800, fontSize: 12 }}>
            Add overlay URLs as 1920×1080 Browser Sources. The tournament promo is designed for your Starting scene.
          </div>
        </div>
      </div>

      <div style={{ flex: 1, minHeight: 0 }}>
        <ChatBox
          key={authed ? "authed" : "guest"}
          room={room}
          role="obs"
          title="CHAT"
          loginHref={`/login?next=${encodeURIComponent(`/obs/chat/${slug}`)}`}
        />
      </div>
    </div>
  );
}